/**
 * Firestore Database Service
 * 
 * Handles all Firestore operations with proper error handling,
 * type safety, and performance optimizations.
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  addDoc,
  onSnapshot,
  writeBatch,
  serverTimestamp,
  DocumentReference as _DocumentReference,
  QuerySnapshot as _QuerySnapshot,
  DocumentSnapshot as _DocumentSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './config';

// Generic Firestore result types
export interface FirestoreResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface FirestoreQueryOptions {
  orderByField?: string;
  orderDirection?: 'asc' | 'desc';
  limitCount?: number;
  whereConditions?: Array<{
    field: string;
    operator: any;
    value: any;
  }>;
}

/**
 * Inspect a Firestore error and, for the failure modes that silently produce
 * "everything is 0" symptoms (denied writes, missing composite indexes), emit a
 * loud, actionable warning instead of a generic logged error. Returns the raw
 * Firebase error code when available.
 */
export const describeFirestoreError = (context: string, error: unknown): string => {
  const code = (error as { code?: string })?.code || '';
  if (code === 'permission-denied') {
    console.error(
      `🔒 [FIRESTORE PERMISSION DENIED] ${context} — this write/read was blocked by ` +
      `security rules. Check firestore.rules covers this collection/field and deploy ` +
      `with: firebase deploy --only firestore:rules`,
      error
    );
  } else if (code === 'failed-precondition') {
    console.error(
      `📑 [FIRESTORE MISSING INDEX] ${context} — a composite index is required. ` +
      `Open the link in the error to create it, or add it to firestore.indexes.json ` +
      `and run: firebase deploy --only firestore:indexes`,
      error
    );
  }
  return code;
};

/**
 * Generic document operations
 */

// Create or update a document
export const setDocument = async <T extends Record<string, any>>(
  collectionName: string,
  documentId: string,
  data: T,
  merge: boolean = false
): Promise<FirestoreResult<T>> => {
  try {
    console.log(`📝 Setting document: ${collectionName}/${documentId}`);
    
    const docRef = doc(db, collectionName, documentId);
    const dataWithTimestamp = {
      ...data,
      updatedAt: serverTimestamp(),
      ...(merge ? {} : { createdAt: serverTimestamp() })
    };
    
    await setDoc(docRef, dataWithTimestamp, { merge });
    
    console.log(`✅ Document set successfully: ${collectionName}/${documentId}`);
    return { success: true, data: dataWithTimestamp as T };
  } catch (error) {
    console.error(`🚫 Failed to set document: ${collectionName}/${documentId}`, error);
    describeFirestoreError(`setDocument ${collectionName}/${documentId}`, error);
    return {
      success: false,
      error: `Failed to save document: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

// Get a single document
export const getDocument = async <T = any>(
  collectionName: string,
  documentId: string
): Promise<FirestoreResult<T>> => {
  try {
    console.log(`📖 Getting document: ${collectionName}/${documentId}`);
    
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = { id: docSnap.id, ...docSnap.data() } as T;
      console.log(`✅ Document retrieved: ${collectionName}/${documentId}`);
      return { success: true, data };
    } else {
      console.log(`📭 Document not found: ${collectionName}/${documentId}`);
      return { success: false, error: 'Document not found' };
    }
  } catch (error) {
    console.error(`🚫 Failed to get document: ${collectionName}/${documentId}`, error);
    return {
      success: false,
      error: `Failed to retrieve document: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

// Update a document
export const updateDocument = async <T extends Record<string, any>>(
  collectionName: string,
  documentId: string,
  data: Partial<T>
): Promise<FirestoreResult<T>> => {
  try {
    console.log(`✏️ Updating document: ${collectionName}/${documentId}`);
    
    const docRef = doc(db, collectionName, documentId);
    const updateData = {
      ...data,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(docRef, updateData);
    
    console.log(`✅ Document updated: ${collectionName}/${documentId}`);
    return { success: true, data: updateData as unknown as T };
  } catch (error) {
    console.error(`🚫 Failed to update document: ${collectionName}/${documentId}`, error);
    describeFirestoreError(`updateDocument ${collectionName}/${documentId}`, error);
    return {
      success: false,
      error: `Failed to update document: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

// Delete a document
export const deleteDocument = async (
  collectionName: string,
  documentId: string
): Promise<FirestoreResult<boolean>> => {
  try {
    console.log(`🗑️ Deleting document: ${collectionName}/${documentId}`);
    
    const docRef = doc(db, collectionName, documentId);
    await deleteDoc(docRef);
    
    console.log(`✅ Document deleted: ${collectionName}/${documentId}`);
    return { success: true, data: true };
  } catch (error) {
    console.error(`🚫 Failed to delete document: ${collectionName}/${documentId}`, error);
    return {
      success: false,
      error: `Failed to delete document: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

// Add a document with auto-generated ID
export const addDocument = async <T extends Record<string, any>>(
  collectionName: string,
  data: T
): Promise<FirestoreResult<T & { id: string }>> => {
  try {
    console.log(`➕ Adding document to: ${collectionName}`);
    
    const collectionRef = collection(db, collectionName);
    const dataWithTimestamp = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collectionRef, dataWithTimestamp);
    
    console.log(`✅ Document added: ${collectionName}/${docRef.id}`);
    return { 
      success: true, 
      data: { id: docRef.id, ...dataWithTimestamp } as T & { id: string }
    };
  } catch (error) {
    console.error(`🚫 Failed to add document to: ${collectionName}`, error);
    describeFirestoreError(`addDocument ${collectionName}`, error);
    return {
      success: false,
      error: `Failed to add document: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Query operations
 */

// Get multiple documents with query options
export const getDocuments = async <T = any>(
  collectionName: string,
  options: FirestoreQueryOptions = {}
): Promise<FirestoreResult<T[]>> => {
  try {
    console.log(`📋 Querying collection: ${collectionName}`, options);
    
    const collectionRef = collection(db, collectionName);
    let q = query(collectionRef);
    
    // Apply where conditions
    if (options.whereConditions) {
      options.whereConditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });
    }
    
    // Apply ordering
    if (options.orderByField) {
      q = query(q, orderBy(options.orderByField, options.orderDirection || 'asc'));
    }
    
    // Apply limit
    if (options.limitCount) {
      q = query(q, limit(options.limitCount));
    }
    
    const querySnapshot = await getDocs(q);
    const documents = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as T[];
    
    console.log(`✅ Retrieved ${documents.length} documents from: ${collectionName}`);
    return { success: true, data: documents };
  } catch (error) {
    console.error(`🚫 Failed to query collection: ${collectionName}`, error);
    describeFirestoreError(`getDocuments ${collectionName}`, error);
    return {
      success: false,
      error: `Failed to query documents: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Real-time listeners
 */

// Listen to document changes
export const listenToDocument = <T = any>(
  collectionName: string,
  documentId: string,
  callback: (data: T | null, error?: string) => void
): Unsubscribe => {
  console.log(`👂 Listening to document: ${collectionName}/${documentId}`);
  
  const docRef = doc(db, collectionName, documentId);
  
  return onSnapshot(
    docRef,
    (doc) => {
      if (doc.exists()) {
        const data = { id: doc.id, ...doc.data() } as T;
        callback(data);
      } else {
        callback(null, 'Document not found');
      }
    },
    (error) => {
      console.error(`🚫 Document listener error: ${collectionName}/${documentId}`, error);
      callback(null, error.message);
    }
  );
};

// Listen to collection changes
export const listenToCollection = <T = any>(
  collectionName: string,
  options: FirestoreQueryOptions = {},
  callback: (data: T[], error?: string) => void
): Unsubscribe => {
  console.log(`👂 Listening to collection: ${collectionName}`, options);
  
  const collectionRef = collection(db, collectionName);
  let q = query(collectionRef);
  
  // Apply query options (same as getDocuments)
  if (options.whereConditions) {
    options.whereConditions.forEach(condition => {
      q = query(q, where(condition.field, condition.operator, condition.value));
    });
  }
  
  if (options.orderByField) {
    q = query(q, orderBy(options.orderByField, options.orderDirection || 'asc'));
  }
  
  if (options.limitCount) {
    q = query(q, limit(options.limitCount));
  }
  
  return onSnapshot(
    q,
    (querySnapshot) => {
      const documents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as T[];
      callback(documents);
    },
    (error) => {
      console.error(`🚫 Collection listener error: ${collectionName}`, error);
      callback([], error.message);
    }
  );
};

/**
 * Batch operations
 */

// Batch write operations
export const batchWrite = async (
  operations: Array<{
    type: 'set' | 'update' | 'delete';
    collection: string;
    id: string;
    data?: any;
  }>
): Promise<FirestoreResult<boolean>> => {
  try {
    console.log(`📦 Executing batch write with ${operations.length} operations`);
    
    const batch = writeBatch(db);
    
    operations.forEach((operation) => {
      const docRef = doc(db, operation.collection, operation.id);
      
      switch (operation.type) {
        case 'set':
          batch.set(docRef, {
            ...operation.data,
            updatedAt: serverTimestamp()
          });
          break;
        case 'update':
          batch.update(docRef, {
            ...operation.data,
            updatedAt: serverTimestamp()
          });
          break;
        case 'delete':
          batch.delete(docRef);
          break;
      }
    });
    
    await batch.commit();
    
    console.log(`✅ Batch write completed: ${operations.length} operations`);
    return { success: true, data: true };
  } catch (error) {
    console.error('🚫 Batch write failed:', error);
    return {
      success: false,
      error: `Batch operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Utility functions
 */

// Check if collection exists
export const collectionExists = async (collectionName: string): Promise<boolean> => {
  try {
    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(query(collectionRef, limit(1)));
    return !snapshot.empty;
  } catch (error) {
    console.error(`🚫 Failed to check collection existence: ${collectionName}`, error);
    return false;
  }
};

// Get document count (approximate)
export const getDocumentCount = async (
  collectionName: string,
  whereConditions?: FirestoreQueryOptions['whereConditions']
): Promise<number> => {
  try {
    const result = await getDocuments(collectionName, { whereConditions });
    return result.data?.length || 0;
  } catch (error) {
    console.error(`🚫 Failed to get document count: ${collectionName}`, error);
    return 0;
  }
};

// Development helpers
if (import.meta.env.DEV) {
  console.log('🛠️ Firestore service initialized in development mode');
}
